import React from 'react';
import { Calendar, Clock, ArrowRight, Scale, TrendingUp, AlertTriangle, Users, Briefcase, GraduationCap, Target, LineChart, Shield, Zap, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

export const AIImpactLawyersPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' ? "L'Impact de l'IA sur le Métier d'Avocat" : "The Impact of AI on the Legal Profession",
    "description": language === 'fr' ? "Analyse stratégique de l'impact de l'intelligence artificielle sur la profession juridique" : "Strategic analysis of AI's impact on the legal profession",
    "author": { "@type": "Organization", "name": "Gaius" },
    "publisher": { "@type": "Organization", "name": "Gaius", "logo": { "@type": "ImageObject", "url": "https://gaius-tech.com/logo.png" } },
    "datePublished": "2026-01-15",
    "dateModified": "2026-01-15"
  };

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead 
          title="Impact de l'IA sur le métier d'avocat en 2026" 
          description="Analyse de l'impact de l'IA sur la profession juridique. Évolutions, opportunités et défis pour les avocats." 
          keywords="impact ia avocat, futur profession juridique, transformation avocat, ia générative avocats, avocat et intelligence artificielle, avenir métier avocat" 
          canonicalUrl="https://gaius-tech.com/articles/impact-ia-avocats" 
          alternateUrlFr="https://gaius-tech.com/articles/impact-ia-avocats"
          alternateUrlEn="https://gaius-tech.com/articles/ai-impact-lawyers"
          pageType="article"
          structuredData={structuredData}
        />
        
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <Badge variant="secondary" className="mb-4 bg-purple-100 text-purple-800">Analyse Stratégique</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              L'impact de l'IA sur le métier d'avocat
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Une analyse équilibrée des transformations en cours et des perspectives d'avenir pour la profession juridique.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>15 janvier 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>18 min de lecture</span>
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <p className="text-xl leading-relaxed mb-8">
              L'émergence de l'<strong>intelligence artificielle générative</strong> suscite des interrogations légitimes au sein de la profession juridique. Après deux années d'adoption croissante, nous disposons désormais de données fiables pour dresser un <strong>bilan nuancé</strong>, éloigné tant du catastrophisme que de l'angélisme technologique.
            </p>

            <p className="mb-8">
              Cet article propose une analyse stratégique destinée aux professionnels du droit souhaitant <strong>anticiper et accompagner</strong> cette transformation plutôt que la subir.
            </p>

            {/* Key Data */}
            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Données clés du marché (2025-2026)</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• <strong>44%</strong> des tâches juridiques potentiellement automatisables (Goldman Sachs, 2024)</li>
                    <li>• <strong>67%</strong> des cabinets du Magic Circle utilisent l'IA au quotidien</li>
                    <li>• <strong>+25%</strong> de productivité moyenne sur la recherche documentaire</li>
                    <li>• <strong>3,2 milliards $</strong> d'investissements dans la Legal Tech en 2025</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Une transformation profonde, pas une disparition</h2>

            <p className="mb-6">
              Premier constat essentiel : l'IA ne <strong>remplace pas</strong> les avocats, elle <strong>transforme</strong> leur métier. Les études récentes convergent sur ce point : les tâches automatisées sont principalement des tâches à faible valeur ajoutée, permettant aux juristes de se concentrer sur l'essentiel.
            </p>

            <p className="mb-6">
              Cette évolution s'inscrit dans un mouvement plus large de <strong>recentrage sur la valeur ajoutée humaine</strong> : le conseil stratégique, la négociation, la relation client, l'innovation juridique.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">Ce qui change concrètement</h3>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <Card className="border-blue-200">
                <CardContent className="p-6">
                  <h4 className="font-bold text-blue-800 mb-4 flex items-center">
                    <Zap className="w-5 h-5 mr-2" />
                    Tâches transformées par l'IA
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Recherche documentaire et jurisprudentielle</li>
                    <li>• Revue de contrats et due diligence</li>
                    <li>• Rédaction de premiers jets standardisés</li>
                    <li>• Veille juridique et réglementaire</li>
                    <li>• Analyse de risques préliminaire</li>
                    <li>• Tri et classification de documents</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-green-200">
                <CardContent className="p-6">
                  <h4 className="font-bold text-green-800 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Compétences qui prennent de la valeur
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Conseil stratégique et business</li>
                    <li>• Négociation et médiation</li>
                    <li>• Plaidoirie et contentieux complexe</li>
                    <li>• Relation client et accompagnement</li>
                    <li>• Innovation et création juridique</li>
                    <li>• Leadership et gestion d'équipe</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Impact sur les différents profils</h2>

            <h3 className="text-2xl font-bold mt-10 mb-4">Collaborateurs juniors</h3>

            <p className="mb-6">
              C'est le segment le plus directement impacté. Les tâches traditionnellement confiées aux jeunes collaborateurs (recherche, revue documentaire, premiers jets) sont les plus automatisables. Cependant, cela représente aussi une <strong>opportunité de montée en compétences accélérée</strong>.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-8">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-semibold text-lg mb-2">Point d'attention</h4>
                  <p className="text-amber-700 mb-0">
                    Les juniors qui maîtrisent l'IA se démarquent significativement. La capacité à utiliser efficacement ces outils devient un critère de recrutement et d'évaluation.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mt-10 mb-4">Collaborateurs seniors et associés</h3>

            <p className="mb-6">
              Pour les profils expérimentés, l'IA est un <strong>multiplicateur de productivité</strong>. Elle permet de traiter plus de dossiers, d'approfondir les analyses, et de consacrer plus de temps au conseil stratégique et à la relation client.
            </p>

            <p className="mb-6">
              Le risque principal est le <strong>déclassement compétitif</strong> : un associé qui refuse d'adopter ces outils se retrouvera en difficulté face à des concurrents plus agiles.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">Avocats indépendants</h3>

            <p className="mb-6">
              L'IA représente une <strong>opportunité majeure d'équipement</strong>. Des outils autrefois réservés aux grands cabinets deviennent accessibles, permettant de concurrencer des structures plus importantes sur certains segments.
            </p>

            {/* Section 3 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Impact sur les domaines de pratique</h2>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Domaine</th>
                    <th className="border border-border p-3 text-center">Impact IA</th>
                    <th className="border border-border p-3 text-left">Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3 font-medium">M&A / Corporate</td>
                    <td className="border border-border p-3 text-center">🔴 Élevé</td>
                    <td className="border border-border p-3 text-sm">Due diligence et revue contractuelle très automatisables</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Contentieux commercial</td>
                    <td className="border border-border p-3 text-center">🟡 Moyen</td>
                    <td className="border border-border p-3 text-sm">Recherche jurisprudentielle automatisée, plaidoirie humaine</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Pénal</td>
                    <td className="border border-border p-3 text-center">🟢 Faible</td>
                    <td className="border border-border p-3 text-sm">Dimension humaine centrale, audiences en présentiel</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Propriété intellectuelle</td>
                    <td className="border border-border p-3 text-center">🟡 Moyen</td>
                    <td className="border border-border p-3 text-sm">Veille et recherche d'antériorités automatisables</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Droit social</td>
                    <td className="border border-border p-3 text-center">🟡 Moyen</td>
                    <td className="border border-border p-3 text-sm">Contentieux prud'homal moins impacté que le conseil</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Droit de la famille</td>
                    <td className="border border-border p-3 text-center">🟢 Faible</td>
                    <td className="border border-border p-3 text-sm">Dimension relationnelle et émotionnelle irremplaçable</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Section 4 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Stratégies d'adaptation recommandées</h2>

            <h3 className="text-2xl font-bold mt-10 mb-4">1. Se former maintenant</h3>

            <p className="mb-6">
              La maîtrise opérationnelle des outils d'IA devient une <strong>compétence professionnelle attendue</strong>. Les cabinets leaders investissent massivement dans la formation de leurs équipes.
            </p>

            <div className="bg-primary/10 p-6 rounded-lg my-8">
              <h4 className="font-semibold mb-4">Parcours de formation recommandé :</h4>
              <ol className="space-y-2">
                <li><strong>1.</strong> Maîtriser les fondamentaux (prompting, cas d'usage)</li>
                <li><strong>2.</strong> Expérimenter les outils spécialisés (Harvey, Lexis AI+, etc.)</li>
                <li><strong>3.</strong> Intégrer l'IA dans son workflow quotidien</li>
                <li><strong>4.</strong> Développer une expertise distinctive</li>
              </ol>
            </div>

            <h3 className="text-2xl font-bold mt-10 mb-4">2. Développer son expertise de niche</h3>

            <p className="mb-6">
              Les <strong>spécialistes pointus</strong> restent irremplaçables. L'IA renforce paradoxalement la valeur de l'expertise humaine approfondie, car elle ne peut reproduire des années d'expérience sectorielle.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">3. Cultiver les soft skills</h3>

            <p className="mb-6">
              Les compétences relationnelles prennent une importance croissante : <strong>empathie, négociation, leadership, communication</strong>. Ce sont les domaines où l'humain conserve un avantage décisif.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4">4. Adopter la posture de l'« avocat augmenté »</h3>

            <p className="mb-6">
              L'objectif n'est pas de résister à l'IA mais de l'<strong>utiliser pour amplifier ses capacités</strong>. L'avocat augmenté combine expertise juridique et maîtrise technologique pour délivrer un service supérieur.
            </p>

            {/* Section 5 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Perspectives économiques</h2>

            <p className="mb-6">
              L'impact économique de l'IA sur le secteur juridique est complexe et multidimensionnel :
            </p>

            <div className="grid md:grid-cols-3 gap-4 my-8">
              <Card>
                <CardContent className="p-4 text-center">
                  <LineChart className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <h4 className="font-bold">Honoraires</h4>
                  <p className="text-sm text-muted-foreground">Pression à la baisse sur les tâches commoditisées, hausse pour le conseil stratégique</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <h4 className="font-bold">Emploi</h4>
                  <p className="text-sm text-muted-foreground">Redistribution plutôt que destruction nette d'emplois</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Target className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <h4 className="font-bold">Concurrence</h4>
                  <p className="text-sm text-muted-foreground">Intensification entre cabinets, nouveaux acteurs Legal Tech</p>
                </CardContent>
              </Card>
            </div>

            {/* Section 6 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Questions éthiques et déontologiques</h2>

            <p className="mb-6">
              L'adoption de l'IA soulève des questions fondamentales pour la profession :
            </p>

            <ul className="list-disc pl-6 space-y-4 mb-8">
              <li>
                <strong>Confidentialité</strong> : comment garantir le secret professionnel avec des outils cloud ?
              </li>
              <li>
                <strong>Responsabilité</strong> : qui est responsable en cas d'erreur générée par l'IA ?
              </li>
              <li>
                <strong>Transparence</strong> : faut-il informer le client de l'utilisation de l'IA ?
              </li>
              <li>
                <strong>Accès au droit</strong> : l'IA démocratise-t-elle ou renforce-t-elle les inégalités ?
              </li>
            </ul>

            <div className="bg-muted/50 border-l-4 border-muted-foreground p-6 my-8">
              <div className="flex items-start">
                <Scale className="w-6 h-6 text-muted-foreground mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-lg mb-2">Position du CNB</h4>
                  <p className="text-muted-foreground mb-0">
                    Le Conseil National des Barreaux travaille sur un cadre déontologique pour l'usage de l'IA. Les principes fondamentaux : l'avocat reste responsable, l'IA est un outil au service du client, la confidentialité doit être garantie.
                  </p>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Conclusion : transformer le défi en opportunité</h2>

            <p className="mb-6">
              L'intelligence artificielle transforme le métier d'avocat sans le remplacer. Les praticiens qui sauront intégrer ces outils dans leur pratique bénéficieront d'un <strong>avantage compétitif significatif</strong>.
            </p>

            <p className="mb-6">
              L'enjeu n'est pas de résister à cette évolution, mais de l'<strong>accompagner avec discernement</strong>. Les avocats qui combinent expertise juridique, maîtrise technologique et compétences relationnelles seront les leaders de demain.
            </p>

            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <Brain className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Notre conviction</h4>
                  <p className="text-foreground/80 mb-0">
                    L'IA ne remplacera pas les avocats. Mais les avocats qui maîtrisent l'IA remplaceront ceux qui l'ignorent. La formation et l'adaptation sont les clés de la réussite dans ce nouveau paradigme.
                  </p>
                </div>
              </div>
            </div>

          </div>
          
          {/* CTA */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold mb-6">Préparez-vous à la transformation</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/training">
                  Découvrir nos formations <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/audit">
                  Demander un audit
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="AI Impact on the Legal Profession in 2026" 
        description="Analysis of AI's impact on the legal profession. Evolutions, opportunities and challenges for lawyers." 
        keywords="ai impact lawyers, future legal profession, lawyer transformation, generative ai lawyers" 
        canonicalUrl="https://gaius-tech.com/articles/ai-impact-lawyers" 
        alternateUrlFr="https://gaius-tech.com/articles/impact-ia-avocats"
        alternateUrlEn="https://gaius-tech.com/articles/ai-impact-lawyers"
        pageType="article"
        structuredData={structuredData}
      />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Badge variant="secondary" className="mb-4 bg-purple-100 text-purple-800">Strategic Analysis</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            The Impact of AI on the Legal Profession
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            A balanced analysis of ongoing transformations and future perspectives for the legal profession.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <span className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>January 15, 2026</span>
            </span>
            <span className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>18 min read</span>
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            The emergence of <strong>generative artificial intelligence</strong> raises legitimate questions within the legal profession. After two years of increasing adoption, we now have reliable data to draw a <strong>nuanced assessment</strong>, far from both catastrophism and technological naivety.
          </p>

          <p className="mb-8">
            This article offers a strategic analysis for legal professionals seeking to <strong>anticipate and accompany</strong> this transformation rather than suffer it.
          </p>

          {/* Key Data */}
          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Key Market Data (2025-2026)</h4>
                <ul className="text-foreground/80 space-y-1 mb-0">
                  <li>• <strong>44%</strong> of legal tasks potentially automatable (Goldman Sachs, 2024)</li>
                  <li>• <strong>67%</strong> of Magic Circle firms use AI daily</li>
                  <li>• <strong>+25%</strong> average productivity on documentary research</li>
                  <li>• <strong>$3.2 billion</strong> Legal Tech investments in 2025</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-12 mb-6">A Deep Transformation, Not Disappearance</h2>

          <p className="mb-6">
            First essential observation: AI does not <strong>replace</strong> lawyers, it <strong>transforms</strong> their profession. Recent studies converge on this point: automated tasks are mainly low value-added tasks, allowing lawyers to focus on what matters.
          </p>

          <p className="mb-6">
            This evolution is part of a broader movement to <strong>refocus on human added value</strong>: strategic advice, negotiation, client relationships, legal innovation.
          </p>

          <h2 className="text-3xl font-bold mt-12 mb-6">Recommended Adaptation Strategies</h2>

          <h3 className="text-2xl font-bold mt-10 mb-4">1. Train Now</h3>

          <p className="mb-6">
            Operational mastery of AI tools is becoming an <strong>expected professional skill</strong>. Leading firms are investing massively in training their teams.
          </p>

          <h3 className="text-2xl font-bold mt-10 mb-4">2. Develop Niche Expertise</h3>

          <p className="mb-6">
            <strong>Deep specialists</strong> remain irreplaceable. AI paradoxically reinforces the value of deep human expertise, as it cannot replicate years of sector experience.
          </p>

          <h3 className="text-2xl font-bold mt-10 mb-4">3. Cultivate Soft Skills</h3>

          <p className="mb-6">
            Relational skills are growing in importance: <strong>empathy, negotiation, leadership, communication</strong>. These are areas where humans retain a decisive advantage.
          </p>

          {/* Conclusion */}
          <h2 className="text-3xl font-bold mt-12 mb-6">Conclusion: Turning Challenge into Opportunity</h2>

          <p className="mb-6">
            Artificial intelligence transforms the legal profession without replacing it. Practitioners who integrate these tools into their practice will benefit from a <strong>significant competitive advantage</strong>.
          </p>

          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <Brain className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Our Conviction</h4>
                <p className="text-foreground/80 mb-0">
                  AI will not replace lawyers. But lawyers who master AI will replace those who ignore it. Training and adaptation are the keys to success in this new paradigm.
                </p>
              </div>
            </div>
          </div>

        </div>
        
        {/* CTA */}
        <div className="mt-12 pt-8 border-t">
          <h3 className="text-xl font-bold mb-6">Prepare for the Transformation</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild>
              <Link to="/training">
                Discover our trainings <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/audit">
                Request an audit
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
